<template>
  <div class="ja-header">
    <!-- Row 1: title left, filters right -->
    <div class="ja-header__row">
      <div class="ja-header__intro">
        <h1 class="ja-header__title">Analytics</h1>
        <p class="ja-header__subtitle">
          Unified funnel &amp; activity insights · {{ formattedRange }}
        </p>
      </div>

      <div class="ja-header__controls">
        <div class="ja-header__dates">
          <div class="ja-header__date-field">
            <feather-icon icon="CalendarIcon" svgClasses="ja-header__date-icon" />
            <datepicker
              :value="dateFrom"
              :disabled-dates="disabledDatesFrom"
              format="dd MMM yyyy"
              input-class="ja-header__date-input"
              placeholder="Start date"
              @selected="onStartDateSelected"
            />
          </div>
          <feather-icon icon="ArrowRightIcon" svgClasses="ja-header__date-arrow" />
          <div class="ja-header__date-field">
            <feather-icon icon="CalendarIcon" svgClasses="ja-header__date-icon" />
            <datepicker
              :value="dateTo"
              :disabled-dates="disabledDatesTo"
              format="dd MMM yyyy"
              input-class="ja-header__date-input"
              placeholder="End date"
              @selected="onEndDateSelected"
            />
          </div>
        </div>

        <button
          type="button"
          class="ja-header__btn ja-header__btn--search"
          :disabled="loading"
          @click="$emit('search')"
        >
          <feather-icon icon="SearchIcon" svgClasses="ja-header__btn-icon" />
          {{ loading ? "Searching..." : "Search" }}
        </button>

        <button
          type="button"
          class="ja-header__btn ja-header__btn--download"
          @click="$emit('download')"
        >
          <feather-icon icon="DownloadIcon" svgClasses="ja-header__btn-icon ja-header__btn-icon--purple" />
          Download
        </button>
      </div>
    </div>

    <!-- Row 2: view toggle -->
    <div class="ja-header__toggle-wrap">
      <div class="ja-header__toggle">
        <button
          type="button"
          class="ja-header__toggle-btn"
          :class="{ 'ja-header__toggle-btn--active': activeView === 'funnel' }"
          @click="$emit('update:activeView', 'funnel')"
        >
          <feather-icon
            icon="FilterIcon"
            :svgClasses="[
              'ja-header__toggle-icon',
              activeView === 'funnel' ? 'ja-header__toggle-icon--active' : '',
            ]"
          />
          Funnel View
        </button>
        <button
          type="button"
          class="ja-header__toggle-btn"
          :class="{ 'ja-header__toggle-btn--active': activeView === 'activity' }"
          @click="$emit('update:activeView', 'activity')"
        >
          <feather-icon
            icon="TrendingUpIcon"
            :svgClasses="[
              'ja-header__toggle-icon',
              activeView === 'activity' ? 'ja-header__toggle-icon--active' : '',
            ]"
          />
          Activity View
        </button>
      </div>
      <p class="ja-header__view-hint">{{ viewHint }}</p>
    </div>
  </div>
</template>

<script>
import Datepicker from "vuejs-datepicker";

export default {
  name: "AnalyticsHeader",
  components: { Datepicker },
  props: {
    dateFrom: { type: Date, required: true },
    dateTo: { type: Date, required: true },
    activeView: { type: String, default: "funnel" },
    loading: { type: Boolean, default: false },
  },
  computed: {
    formattedRange() {
      const opts = { day: "numeric", month: "short", year: "numeric" };
      const from = this.dateFrom
        ? this.dateFrom.toLocaleDateString("en-GB", opts)
        : "";
      const to = this.dateTo
        ? this.dateTo.toLocaleDateString("en-GB", opts)
        : "";
      return `${from} – ${to}`;
    },
    viewHint() {
      if (this.activeView === "activity") {
        return "Metrics are calculated based on when each event occurred, providing a day-by-day view of customer activity such as link opens, clicks, payments, and other interactions.";
      }
      return "Metrics are calculated based on the date the NudgPay link was generated. All subsequent customer actions (opens, pay clicks, and payments) are attributed back to that original link, regardless of when they occurred.";
    },
    disabledDatesFrom() {
      const end = this.startOfDay(this.dateTo);
      const today = this.startOfDay(new Date());

      return {
        customPredictor: (date) => {
          const day = this.startOfDay(date);
          if (day > today) return true;
          if (end && day > end) return true;
          return false;
        },
      };
    },
    disabledDatesTo() {
      const start = this.startOfDay(this.dateFrom);
      const today = this.startOfDay(new Date());

      return {
        customPredictor: (date) => {
          const day = this.startOfDay(date);
          if (day > today) return true;
          if (start && day < start) return true;
          return false;
        },
      };
    },
  },
  watch: {
    dateFrom(val) {
      this.enforceDateRange(val, this.dateTo);
    },
    dateTo(val) {
      this.enforceDateRange(this.dateFrom, val);
    },
  },
  methods: {
    startOfDay(date) {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d;
    },
    isBefore(dateA, dateB) {
      return this.startOfDay(dateA) < this.startOfDay(dateB);
    },
    isSameDay(dateA, dateB) {
      return this.startOfDay(dateA).getTime() === this.startOfDay(dateB).getTime();
    },
    onStartDateSelected(date) {
      if (!date) return;

      this.$emit("update:dateFrom", date);

      if (this.dateTo && this.isBefore(this.dateTo, date)) {
        this.$emit("update:dateTo", new Date(date));
      }
    },
    onEndDateSelected(date) {
      if (!date) return;

      if (this.dateFrom && this.isBefore(date, this.dateFrom)) {
        this.$emit("update:dateTo", new Date(this.dateFrom));
        return;
      }

      this.$emit("update:dateTo", date);
    },
    enforceDateRange(from, to) {
      if (!from || !to) return;

      if (this.isBefore(to, from)) {
        const corrected = new Date(from);
        if (!this.isSameDay(to, corrected)) {
          this.$emit("update:dateTo", corrected);
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
.ja-header {
  margin-bottom: 1.75rem;

  &__row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.5rem;
    flex-wrap: wrap;
  }

  &__intro {
    flex: 1 1 280px;
    min-width: 0;
  }

  &__title {
    font-size: 2rem;
    font-weight: 700;
    color: #2d2d2d;
    margin: 0 0 0.35rem;
    line-height: 1.2;
    letter-spacing: -0.02em;
  }

  &__subtitle {
    font-size: 0.875rem;
    color: #a8a8b3;
    margin: 0;
    line-height: 1.4;
  }

  &__controls {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.65rem;
    flex: 0 1 auto;
    justify-content: flex-end;
  }

  &__dates {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__date-field {
    position: relative;
    display: flex;
    align-items: center;
    background: #fff;
    border: 1px solid #e4e4e8;
    border-radius: 10px;
    padding: 0 0.75rem 0 0.65rem;
    min-height: 42px;
    min-width: 148px;
    transition: border-color 0.15s ease;

    &:focus-within {
      border-color: #c8c4f0;
      box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.08);
    }

    :deep(.vdp-datepicker) {
      width: 100%;
      min-width: 0;

      input {
        border: none !important;
        outline: none !important;
        box-shadow: none !important;
        background: transparent !important;
        padding: 0.55rem 0 !important;
        font-size: 0.875rem !important;
        color: #5e5873 !important;
        width: 100%;
        min-width: 0;
        cursor: pointer;
        border-radius: 0 !important;
      }
    }
  }

  &__date-icon {
    width: 16px;
    height: 16px;
    color: #b9b9c3;
    flex-shrink: 0;
    margin-right: 0.4rem;
  }

  &__date-arrow {
    width: 16px;
    height: 16px;
    color: #c4c4cc;
    flex-shrink: 0;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    border: none;
    border-radius: 10px;
    font-size: 0.875rem;
    font-weight: 500;
    min-height: 42px;
    padding: 0 1.1rem;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;

    &:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }
  }

  &__btn-icon {
    width: 16px;
    height: 16px;
    color: #fff;

    &--purple {
      color: #7367f0;
    }
  }

  &__btn--search {
    background: #7367f0;
    color: #fff;
    box-shadow: 0 4px 14px rgba(115, 103, 240, 0.28);

    &:hover:not(:disabled) {
      background: #685dd8;
    }
  }

  &__btn--download {
    background: #fff;
    color: #7367f0;
    border: 1px solid #e4e4e8;

    &:hover {
      border-color: #d0cce8;
      background: #faf9ff;
    }
  }

  &__toggle-wrap {
    margin-top: 1.25rem;
  }

  &__toggle {
    display: inline-flex;
    align-items: center;
    width: 360px;
    max-width: 100%;
    background: #f1f0f5;
    border-radius: 10px;
    padding: 4px;
    gap: 0;
    box-sizing: border-box;
  }

  &__view-hint {
    margin: 0.75rem 0 0;
    width: 950px;
    max-width: 100%;
    min-height: 46px;
    font-weight: 200;
    font-size: 14.5px;
    line-height: 159%;
    letter-spacing: 0;
    vertical-align: middle;
    color: #8a8fa3;
  }

  &__toggle-btn {
    flex: 1 1 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    border: none;
    background: transparent;
    min-height: 40px;
    padding: 0.55rem 1rem;
    border-radius: 10px;
    font-size: 0.875rem;
    font-weight: 500;
    color: #8b8a96;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
    line-height: 1;
    white-space: nowrap;

    &--active {
      background: #fff;
      color: #6366f1;
      box-shadow: 0 1px 3px rgba(20, 21, 34, 0.08), 0 4px 10px rgba(20, 21, 34, 0.04);
    }
  }

  &__toggle-icon {
    width: 16px;
    height: 16px;
    color: #9ca3af;
    flex-shrink: 0;

    &--active {
      color: #6366f1;
    }
  }
}

@media (max-width: 1100px) {
  .ja-header__controls {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 640px) {
  .ja-header__dates {
    width: 100%;
    flex-wrap: wrap;
  }

  .ja-header__date-field {
    flex: 1 1 130px;
  }

  .ja-header__btn {
    flex: 1 1 auto;
  }
}
</style>

<style lang="scss">
/* Datepicker popup — unscoped so calendar dropdown renders correctly */
.ja-header .vdp-datepicker__calendar {
  border-radius: 10px;
  border: 1px solid #e4e4e8;
  box-shadow: 0 8px 24px rgba(34, 41, 47, 0.1);
}
</style>
